<template>
  <div
    :style="{ height: computedHeight, width: menuWidth }"
    class="menu"
    :class="[{ 'menu--searchable': searchable }, { 'menu--full-width': fullWidth }]"
  >
    <UIInputSearch
      v-if="searchable"
      ref="searchRef"
      v-model="search"
      autocomplete="new"
      name="menu-search"
      :placeholder="searchPlaceholder"
      :compact
      sync-v-model
      :controlled="false"
    />
    <div ref="list" class="menu__list">
      <div v-if="customContent" class="menu__scroller">
        <slot name="custom" />
      </div>
      <div v-else class="menu__scroller" :prerender="10">
        <div v-if="!virtualScroll">
          <slot
            v-for="item in filteredItems"
            :key="`${item.name}-${item[props.valueKey]}`"
            :active="getIsItemActive(item)"
            :item="item"
          />
        </div>
        <UIVirtualScroll v-else :visible-items-list="filteredItems">
          <template #default="{ item }">
            <slot :key="`${item.name}-${item[props.valueKey]}`" :active="getIsItemActive(item)" :item="item" />
          </template>
        </UIVirtualScroll>
      </div>
      <div v-if="!filteredItems.length && !customContent" class="menu__no-result">
        {{ getResultText }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import type { MenuProps } from '~/types/ui'

import type { UIInputSearch } from '#build/components'

const props = withDefaults(defineProps<MenuProps>(), {
  noResultText: 'No results',
  searchPlaceholder: 'Search',
  valueKey: 'id',
  itemSize: 48,
  customContent: false,
  menuWidth: '300px',
  searchKey: () => ['name'],
  compact: false,
  virtualScroll: false,
})

const itemSizePx = `${props.itemSize}px`

const searchHeight = props.compact ? 40 : 48
const searchHeightPx = `${searchHeight}px`
const basePadding = 4
const baseBorderWidth = 1.5
const listMarginTop = 4
const totalPaddingHeight = basePadding * 2
const totalBorderHeight = baseBorderWidth * 2

const defaultMenuHeight = computed(() => {
  return props.menuHeight ? Number(props.menuHeight?.replace('px', '')) : 300
})

defineSlots<{
  default(props: { item: any; active: boolean; key: any }): any
  custom(): any
}>()

const list = ref<HTMLDivElement | null>(null)

const search = ref('')
const searchRef = ref<InstanceType<typeof UIInputSearch> | null>(null)

const { filter } = useFilterItems()

const searchValue = computed(() => {
  return props.searchValue || search.value
})

const filteredItems = computed(() => {
  if (!props.items) return []

  return searchValue.value ? filter(props.items, searchValue.value, props.searchKey) : props.items
})

const getResultText = computed(() => {
  if (!filteredItems.value.length && searchValue.value) {
    return props.noResultText
  }

  if (props.loading) {
    return 'Loading...'
  }

  return 'No options'
})

const computedHeight = computed(() => {
  if (props.customContent) {
    return ''
  }

  const itemsHeight = filteredItems.value.length * props.itemSize

  if (props.searchable) {
    if (!filteredItems.value.length) {
      return `${props.itemSize + searchHeight + totalPaddingHeight + listMarginTop + totalBorderHeight}px`
    }

    return `${Math.min(itemsHeight + totalPaddingHeight + totalBorderHeight + searchHeight + listMarginTop, defaultMenuHeight.value)}px`
  }

  if (!filteredItems.value.length) {
    return `${props.itemSize + totalPaddingHeight + totalBorderHeight}px`
  }

  return `${Math.min(itemsHeight + totalPaddingHeight + totalBorderHeight, defaultMenuHeight.value)}px`
})

const getIsItemActive = (item: any) => {
  if (!props.modelValue) {
    return false
  }

  if (props.multiple) {
    return (props.modelValue as number[]).includes(item[props.valueKey])
  }

  // * Because props.modelValue is parsed from query params as string
  // eslint-disable-next-line eqeqeq
  return item[props.valueKey] == props.modelValue || isEqual(props.modelValue, item[props.valueKey])
}

watchEffect(() => {
  if (!props.menuVisible) {
    search.value = ''
  }
})

defineExpose({ list, searchRef })
</script>

<style scoped lang="scss">
@import '@/styles/mixins.scss';

$base-padding: 4px;
$list-margin-top: 4px;

.menu {
  background: white;
  z-index: 8;
  box-shadow: $base-box-shadow;
  border-radius: $base-border-radius;
  border: $base-border-width solid $black-10;
  padding: $base-padding;
  width: 300px;

  &--searchable {
    .menu__list {
      margin-top: $list-margin-top;
      height: calc(100% - v-bind(searchHeightPx) - $list-margin-top);
    }
  }

  &--full-width {
    width: 100%;
  }

  &__scroller {
    height: 100%;
  }

  &__list {
    margin-top: 0;
    height: 100%;
    overflow-y: auto;
    position: relative;
    z-index: 3;

    @include baseScrollbar();
  }

  &__no-result {
    font-size: 14px;
    color: $black-80;
    height: v-bind(itemSizePx);
    display: flex;
    align-items: center;
    padding-left: calc(16px - $base-padding);
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>
